import React, { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import TextField from '@material-ui/core/TextField'
import { StylesProvider, createMuiTheme, ThemeProvider } from '@material-ui/core/styles'
import { toast } from 'react-toastify'
import axios from 'axios'
import { ButtonStyles } from './ButtonStyles'
import { emailSubmit } from './utils/emailSubmit'
import { hubspotSubmit } from './utils/hubspotSubmit'

const theme = createMuiTheme({
  typography: {
    fontFamily: ['Twentieth Century', 'sans-serif'].join(','),
    fontSize: 12,
  },
})

const HubspotFormEndpoint = `https://api.hsforms.com/submissions/v3/integration/submit/7898466/ae2fe5c8-9983-4039-bfd4-f7434952132f`
// const APIEndpoint = `http://localhost:3000/api/product-enquiry`
const APIEndpoint = `https://api.armansfinejewellery.com/api/product-enquiry`

interface EnquireFormFieldsProps {
  open: boolean
}

interface FormFields {
  [key: string]: string
}

const EnquireFormFields = ({ open }: EnquireFormFieldsProps) => {
  const { register, handleSubmit, watch, errors } = useForm()
  // window.location.href + regex url for last block -> replace - with ' '

  const onSubmit = async (data: any) => {
    // console.log('Errors', errors)

    hubspotSubmit(data, HubspotFormEndpoint)
    emailSubmit(data, APIEndpoint)

    toast.success(`Thanks ${data.firstname}, Your enquiry has been submitted`)
  }

  const variants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.07,
      },
    },
    closed: {
      opacity: 0,
      y: '20%',
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  }

  const divVariants = {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <StyledFormContainer animate={open ? 'open' : 'closed'} variants={variants}>
        <EnquireFormHeader className="header">Enquire Now</EnquireFormHeader>
        <InputGroup key="firstname" variants={divVariants}>
          <Input
            variant="outlined"
            label="First Name"
            margin="dense"
            fullWidth
            inputRef={register({ required: true })}
            name="firstname"
            error={!!errors.firstname}
            helperText={errors.firstname && 'We need at least a first name!'}
          />
        </InputGroup>
        <InputGroup key="lastName" variants={divVariants}>
          <Input variant="outlined" label="Last Name" margin="dense" fullWidth inputRef={register} name="lastname" />
        </InputGroup>
        <InputGroup key="phone" variants={divVariants}>
          <Input variant="outlined" label="Phone Number" margin="dense" fullWidth inputRef={register} name="phone" />
        </InputGroup>
        <InputGroup key="email" variants={divVariants}>
          <Input
            variant="outlined"
            label="Email"
            margin="dense"
            fullWidth
            inputRef={register({ required: true })}
            name="email"
            type="email"
            error={!!errors.email}
            helperText={errors.email && 'Looks like you forgot your email'}
          />
        </InputGroup>
        <InputGroup key="Message" variants={divVariants}>
          <Input variant="outlined" label="Message" margin="dense" fullWidth multiline rows={4} inputRef={register} name="message" />
        </InputGroup>
        <SubmitButton type="submit"></SubmitButton>
      </StyledFormContainer>
    </Form>
  )
}

const Input = styled(TextField)`
  & .MuiFormLabel-root {
    font-family: Slate, sans-serif;
    font-weight: 100;
  }
`

const Form = styled.form`
  display: flex;
  justify-content: center;
`

const StyledFormContainer = styled(motion.div)`
  position: absolute;
  z-index: 100;
  /* border: 1px solid #dbf1ef; */
  max-width: 32rem;
  margin: 6rem 0 0 2rem;
  box-shadow: 7px 7px 20px #dbf1ef;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  padding: 1rem 2rem;
  top: 0;
  opacity: 0;
  background: white;
`

const EnquireFormHeader = styled.h2`
  width: 100%;
  padding: 0 0 1.5rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`

const InputGroup = styled(motion.div)`
  border-radius: 5px;
  color: white;
  width: 44%;

  @media (max-width: 768px) {
    width: 94%;
  }

  &:nth-last-child(2) {
    width: 94%;
  }
`

const EnquireNowForm = () => {
  const [open, setOpen] = useState(false)

  const shareButtons: HTMLElement | null = document.querySelector('.share-buttons-inverted')
  const description: HTMLElement | null = document.querySelector('.product-description')

  // setup some nice transitions for these

  if (shareButtons) {
    shareButtons.style.transition = '0.2s opacity ease-in'
    open ? (shareButtons.style.opacity = '0.2') : (shareButtons.style.opacity = '1')
    open ? (shareButtons.style.filter = 'blur(1px)') : (shareButtons.style.filter = 'blur(0px)')
  }

  if (description) {
    description.style.transition = '0.2s opacity ease-in'
    open ? (description.style.opacity = '0.2') : (description.style.opacity = '1')
    open ? (description.style.filter = 'blur(1px)') : (description.style.filter = 'blur(0px)')
  }

  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Wrapper>
          <Button
            onClick={() => {
              setOpen(!open)
            }}
          >
            Enquire Now
          </Button>
          <FormWrapper>
            <EnquireFormFields open={open} />
          </FormWrapper>
        </Wrapper>
      </ThemeProvider>
    </StylesProvider>
  )
}

export { EnquireNowForm }

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1rem;
`

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const Button = styled.div`
  ${ButtonStyles}
`

const SubmitButton = styled.input`
  ${ButtonStyles}
  margin-top: 2rem;
`
